import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from "react-toastify";
import DashboardLayout from './layout/dashboardLayout';
import LoadingOverlay from '../components/loading/loading';
import service from '../services/service';
import $ from "jquery";
import "select2/dist/css/select2.min.css";
import "select2/dist/js/select2.min.js";
import './styles.css';

const HotelSequencePage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState("");
    const [selectedHotelSet, setSelectedHotelSet] = useState("");
    const [specialSetHotels, setSpecialSetHotels] = useState([]);
    const [set1Hotels, setSet1Hotels] = useState([]);
    const [set2Hotels, setSet2Hotels] = useState([]);
    const [set3Hotels, setSet3Hotels] = useState([]);
    const [set4Hotels, setSet4Hotels] = useState([]);
    const [set5Hotels, setSet5Hotels] = useState([]);
    const [savedSequence, setSavedSequence] = useState([]);
    const [highlightedHotelId, setHighlightedHotelId] = useState(null);
    const selectRef = useRef(null);

    const handleUserChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedUser(selectedValue);
    };

    useEffect(() => {
        const selectElement = $(selectRef.current);

        selectElement.select2({
            placeholder: "Select User",
            allowClear: true,
            width: "100%", // Ensure it takes full width
        });

        selectElement.on("change", function () {
            setSelectedUser($(this).val());
        });

        return () => {
            selectElement.off("change");
            selectElement.select2("destroy");
        };
    }, [users]);

    useEffect(() => {
        $(selectRef.current).val(selectedUser).trigger("change");
    }, [selectedUser]);

    // Create a ref to store the current value of savedSequence without causing rerenders
    const savedSequenceRef = useRef(savedSequence);

    const fetchsavedSequence = useCallback(async () => {
        setLoading(true);
        try {
            const response = await service.view(`hotel-sequence/user/${selectedUser}`);
            if (!response.success) {
                setSelectedHotelSet("");
                setSavedSequence([]);
                return;
            }
            setSavedSequence(response.data.hotelSets);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [selectedUser]);

    const fetchHotels = useCallback(async () => {
        if (!selectedUser) return;

        setLoading(true);
        try {
            const response = await service.view("hotels/list");
            if (!response.success) {
                toast.error(response.message);
                return;
            }

            const allHotels = response.data.hotels;
            const specialSetHotels = allHotels.filter(hotel => hotel.category === "specialSet");

            if (savedSequenceRef.current.length > 0) {
                const savedHotels = savedSequenceRef.current.filter(hotel =>
                    ["set1", "set2", "set3", "set4", "set5", "specialSet"].includes(hotel.category)
                );

                const distinctCategories = [...new Set(savedHotels.map(hotel => hotel.category))];

                // Function to process each set dynamically
                const processHotelSet = (setCategory, setState, excludeCategory) => {
                    if (distinctCategories.includes(setCategory)) {
                        setSelectedHotelSet(setCategory);

                        // Merge setCategory hotels with specialSet hotels
                        const setHotels = savedSequenceRef.current.reduce((acc, hotel) => {
                            if (hotel.category === setCategory) {
                                acc.push(hotel);
                            } else if (hotel.category === "specialSet") {
                                acc.push({ ...hotel, isFromSpecialSet: true });
                            }
                            return acc;
                        }, []);
                        setState(setHotels);

                        // Process other sets excluding the current category
                        ["set1", "set2", "set3", "set4", "set5"].forEach(otherCategory => {
                            if (otherCategory !== excludeCategory) {
                                const otherSetHotels = allHotels.filter(
                                    hotel => hotel.category === otherCategory
                                );
                                switch (otherCategory) {
                                    case "set1":
                                        setSet1Hotels(otherSetHotels);
                                        break;
                                    case "set2":
                                        setSet2Hotels(otherSetHotels);
                                        break;
                                    case "set3":
                                        setSet3Hotels(otherSetHotels);
                                        break;
                                    case "set4":
                                        setSet4Hotels(otherSetHotels);
                                        break;
                                    case "set5":
                                        setSet5Hotels(otherSetHotels);
                                        break;
                                    default:
                                        break;
                                }
                            }
                        });

                        // Find remaining specialSet hotels
                        const remainingSpecialHotels = specialSetHotels.filter(
                            hotel => !setHotels.some(setHotel => setHotel._id === hotel._id)
                        );
                        setSpecialSetHotels(remainingSpecialHotels);
                    }
                };

                // Process each hotel set while ensuring other sets are updated dynamically
                processHotelSet("set1", setSet1Hotels, "set1");
                processHotelSet("set2", setSet2Hotels, "set2");
                processHotelSet("set3", setSet3Hotels, "set3");
                processHotelSet("set4", setSet4Hotels, "set4");
                processHotelSet("set5", setSet5Hotels, "set5");
            } else {
                // If no saved sequence, load directly from API
                setSpecialSetHotels(specialSetHotels.map(hotel => ({ ...hotel, isFromSpecialSet: true })));
                setSet1Hotels(allHotels.filter(hotel => hotel.category === "set1"));
                setSet2Hotels(allHotels.filter(hotel => hotel.category === "set2"));
                setSet3Hotels(allHotels.filter(hotel => hotel.category === "set3"));
                setSet4Hotels(allHotels.filter(hotel => hotel.category === "set4"));
                setSet5Hotels(allHotels.filter(hotel => hotel.category === "set5"));
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [selectedUser]);


    // Update the savedSequenceRef whenever the state changes
    useEffect(() => {
        savedSequenceRef.current = savedSequence;
    }, [savedSequence]);

    useEffect(() => {
        if (selectedUser) {
            fetchsavedSequence().then(fetchHotels);
        } else {
            fetchHotels();
        }
        fetchUsers();
    }, [selectedUser, fetchsavedSequence, fetchHotels]);


    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await service.view('admin/users');
            if (!response.success) {
                toast.error(response.message);
                return;
            }
            setUsers(response.data.users);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const moveHotelToSet = (hotel, targetSet) => {
        // Mapping object for the sets
        const setMapping = {
            set1: setSet1Hotels,
            set2: setSet2Hotels,
            set3: setSet3Hotels,
            set4: setSet4Hotels,
            set5: setSet5Hotels,
            specialSet: setSpecialSetHotels
        };

        // Check if the hotel was originally from Special Set
        const wasFromSpecialSet = hotel.category === "specialSet" || hotel.isFromSpecialSet;

        // Remove hotel from all sets
        Object.values(setMapping).forEach(setState => {
            setState(prev => prev.filter(h => h._id !== hotel._id));
        });

        // Updated hotel object
        const updatedHotel = {
            ...hotel,
            category: targetSet,
            isFromSpecialSet: wasFromSpecialSet // Preserve special set tracking
        };

        // Add hotel to the target set
        if (setMapping[targetSet]) {
            setMapping[targetSet](prev => [...prev, updatedHotel]);
        }
    };


    const moveHotelUp = (hotel, setName) => {
        const stateMapping = {
            set1: { state: setSet1Hotels, current: set1Hotels },
            set2: { state: setSet2Hotels, current: set2Hotels },
            set3: { state: setSet3Hotels, current: set3Hotels },
            set4: { state: setSet4Hotels, current: set4Hotels },
            set5: { state: setSet5Hotels, current: set5Hotels }
        };

        const { state: setState, current: currentSet } = stateMapping[setName] || {};

        if (!setState || !currentSet) return; // If the setName is invalid

        const index = currentSet.findIndex(h => h._id === hotel._id);
        if (index > 0) {
            const updatedSet = [...currentSet];
            [updatedSet[index], updatedSet[index - 1]] = [updatedSet[index - 1], updatedSet[index]];
            setState(updatedSet);
            setHighlightedHotelId(hotel._id);
            setTimeout(() => setHighlightedHotelId(null), 1000);
        }
    };

    const moveHotelDown = (hotel, setName) => {
        const stateMapping = {
            set1: { state: setSet1Hotels, current: set1Hotels },
            set2: { state: setSet2Hotels, current: set2Hotels },
            set3: { state: setSet3Hotels, current: set3Hotels },
            set4: { state: setSet4Hotels, current: set4Hotels },
            set5: { state: setSet5Hotels, current: set5Hotels }
        };

        const { state: setState, current: currentSet } = stateMapping[setName] || {};

        if (!setState || !currentSet) return; // If the setName is invalid

        const index = currentSet.findIndex(h => h._id === hotel._id);
        if (index < currentSet.length - 1) {
            const updatedSet = [...currentSet];
            [updatedSet[index], updatedSet[index + 1]] = [updatedSet[index + 1], updatedSet[index]];
            setState(updatedSet);
            setHighlightedHotelId(hotel._id);
            setTimeout(() => setHighlightedHotelId(null), 1000);
        }
    };

    const saveHotelSequence = async () => {
        if (!selectedUser || !selectedHotelSet) {
            toast.error("Please select a user and hotel set.");
            return;
        }

        const hotelSets = {
            set1: set1Hotels,
            set2: set2Hotels,
            set3: set3Hotels,
            set4: set4Hotels,
            set5: set5Hotels
        };

        const sequence = (hotelSets[selectedHotelSet] || []).map(hotel => hotel._id);

        try {
            setLoading(true);
            const payload = { userId: selectedUser, category: selectedHotelSet, sequence };
            const response = await service.save(payload, "hotel-sequence/");
            if (!response.success) {
                toast.error("Error saving hotel sequence.");
                return;
            }
            toast.success("Hotel sequence saved successfully!");

            setSelectedHotelSet("");
            setSelectedUser("");
            setSavedSequence([]);
            fetchHotels();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const cancelHotelSequence = async () => {
        setSelectedHotelSet("");
        setSelectedUser("");
        setSavedSequence([]);
        // fetchHotels();
    }

    // const handleUserChange = (event) => {
    //     const selectedValue = event.target.value;
    //     setSelectedUser(selectedValue);
    // };

    const handleHotelSetChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedHotelSet(selectedValue);
    };


    return (
        <DashboardLayout>
            {loading && <LoadingOverlay />}
            <div className="container">
                <h3 className="text-center font-semibold mb-4">Manage Hotel Sequence</h3>

                <div className="row">
                    <div className="col-12 text-end">
                        <small><strong>Note:</strong> <span style={{ color: "red" }}>Only 1st 30 hotels from the sequence will be displayed to the user for booking.</span></small>
                    </div>
                    <div className="col-12 col-md-4 mb-4">
                        <label>Select User</label>
                        <select ref={selectRef} className="form-control" value={selectedUser} onChange={handleUserChange}>
                            <option value="">Select User</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>{user.firstName} {user.lastName}</option>
                            ))}
                        </select>
                    </div>

                    <div className="col-12 col-md-4 mb-4">
                        <label>Select Hotel Set</label>
                        <select className="form-control" value={selectedHotelSet} onChange={handleHotelSetChange}>
                            <option value="">Select Set</option>
                            <option value="set1">Set 1</option>
                            <option value="set2">Set 2</option>
                            <option value="set3">Set 3</option>
                            <option value="set4">Set 4</option>
                            <option value="set5">Set 5</option>
                        </select>
                    </div>

                    <div className="col-12 col-md-2 mb-4">
                        <button className="btn btn-primary mt-4 w-100" onClick={saveHotelSequence}>
                            <i className="fa fa-save mr-2"></i> Save Sequence
                        </button>
                    </div>
                    <div className="col-12 col-md-2 mb-4">
                        <button className="btn btn-danger mt-4 w-100" onClick={cancelHotelSequence}>
                            <i className="fa fa-times-circle mr-2"></i> Cancel
                        </button>
                    </div>
                </div>

                <div className="row">
                    {selectedHotelSet && selectedUser && (
                        <>
                            <div className="col-12 col-md-6">
                                <h4>{selectedHotelSet === "set1" ? "Set 1" :
                                    selectedHotelSet === "set2" ? "Set 2" :
                                        selectedHotelSet === "set3" ? "Set 3" :
                                            selectedHotelSet === "set4" ? "Set 4" : "Set 5"} Hotels</h4>

                                {(
                                    selectedHotelSet === "set1" ? set1Hotels :
                                        selectedHotelSet === "set2" ? set2Hotels :
                                            selectedHotelSet === "set3" ? set3Hotels :
                                                selectedHotelSet === "set4" ? set4Hotels : set5Hotels
                                ).map((hotel, index) => (
                                    <div key={hotel._id} className={`d-flex justify-content-between mb-2 ${highlightedHotelId === hotel._id ? 'bg-warning' : ''}`}>
                                        <span className="flex-grow-1">{index + 1}. {hotel.name} - ₹ {hotel.pricePerNight}</span>
                                        <div className="d-flex align-items-center">
                                            <button className="btn btn-sm btn-success" onClick={() => moveHotelUp(hotel, selectedHotelSet)}>↑</button>
                                            <button className="btn btn-sm btn-danger mx-2" onClick={() => moveHotelDown(hotel, selectedHotelSet)}>↓</button>
                                            {hotel.isFromSpecialSet && (
                                                <button className="btn btn-sm btn-warning" onClick={() => moveHotelToSet(hotel, "specialSet")}>↺</button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="col-12 col-md-6">
                                <h4>Special Set Hotels</h4>
                                {specialSetHotels.map(hotel => (
                                    <div key={hotel._id} className="d-flex justify-content-between mb-2">
                                        <span className="flex-grow-1">{hotel.name} - ₹ {hotel.pricePerNight}</span>
                                        <div className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                                            <button className="btn btn-sm btn-warning" style={{ minWidth: '70px' }} onClick={() => moveHotelToSet(hotel, selectedHotelSet)}>← Move</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default HotelSequencePage;